import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { generateMetaTags, getCanonicalUrl } from '../utils/seo';

/**
 * SEO Component
 * Manages dynamic meta tags for each page
 * Updates title, description, canonical URLs, Open Graph, and Twitter Card tags
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  noindex = false
}) => {
  const location = useLocation();
  const currentUrl = getCanonicalUrl(location.pathname);
  const metaTags = generateMetaTags({
    title,
    description,
    keywords,
    image,
    url: currentUrl,
    type,
    noindex
  });

  useEffect(() => {
    // Update document title
    document.title = metaTags.title;

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update standard meta tags
    updateMetaTag('description', metaTags.description);
    updateMetaTag('keywords', metaTags.keywords);
    updateMetaTag('robots', metaTags.robots);

    // Update Open Graph tags
    updateMetaTag('og:title', metaTags['og:title'], true);
    updateMetaTag('og:description', metaTags['og:description'], true);
    updateMetaTag('og:type', metaTags['og:type'], true);
    updateMetaTag('og:url', metaTags['og:url'], true);
    updateMetaTag('og:site_name', metaTags['og:site_name'], true);
    updateMetaTag('og:locale', metaTags['og:locale'], true);
    updateMetaTag('og:image', metaTags['og:image'], true);

    // Update Twitter Card tags
    updateMetaTag('twitter:card', metaTags['twitter:card']);
    updateMetaTag('twitter:title', metaTags['twitter:title']);
    updateMetaTag('twitter:description', metaTags['twitter:description']);
    updateMetaTag('twitter:image', metaTags['twitter:image']);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', metaTags.canonical);

    // Cleanup function (optional, but good practice)
    return () => {
      // Meta tags will persist, which is fine for SEO
      // We could reset to defaults here if needed
    };
  }, [metaTags, location.pathname]);

  return null; // This component doesn't render anything
};

export default SEO;
